import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, Lock, Eye, Database, UserCheck } from "lucide-react";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Logo } from "@/components/Logo";

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - LoopBaan.ai</title>
        <meta name="description" content="LoopBaan.ai Privacy Policy. Learn how we collect, use, and protect your personal information and resume data." />
        <link rel="canonical" href="https://loopbaan.ai/privacy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <MegaMenuNav />

        {/* Hero */}
        <section className="border-b bg-muted/30 py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mb-6 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h1 className="mb-4 text-4xl font-bold md:text-5xl">Privacy Policy for LoopBaan.ai</h1>
              <p className="text-lg text-muted-foreground">
                Last Updated: October 25, 2025 | Effective Date: October 25, 2025
              </p>
              <p className="mt-4 text-sm text-muted-foreground max-w-3xl mx-auto">
                LoopBaan.ai LLC ("we," "us," or "our") operates loopbaan.ai (the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our AI-powered resume and cover letter generation service.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              
              {/* Key Commitments */}
              <Card className="mb-12 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-8">
                <h2 className="mb-6 text-2xl font-bold">Our Privacy Commitments</h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {[
                    { icon: Lock, title: "You Own Your Data", desc: "All resumes and cover letters you create belong to you, forever." },
                    { icon: Eye, title: "No Selling", desc: "We never sell your personal information to third parties." },
                    { icon: Database, title: "Secure Storage", desc: "Your data is encrypted and stored securely." },
                    { icon: UserCheck, title: "Full Control", desc: "Delete your account and data anytime." }
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3">
                      <item.icon className="h-6 w-6 flex-shrink-0 text-primary" />
                      <div>
                        <h3 className="mb-1 font-semibold">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="mb-8 text-center">
                <p className="text-muted-foreground">
                  By using LoopBaan, you agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
              </div>

              {/* Sections */}
              <div className="prose prose-slate max-w-none space-y-8">
                
                {/* Section 1 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">1. Information We Collect</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">1.1 Information You Provide Directly</h3>
                    <p className="mb-4 text-muted-foreground">
                      When you create an account and use our Service, we collect:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Account Information:</strong> Name, email address, password (encrypted)</li>
                      <li><strong className="text-foreground">Professional Information:</strong> Work history, job titles, employment dates, company names, job descriptions</li>
                      <li><strong className="text-foreground">Educational Information:</strong> School names, degrees, graduation dates, fields of study</li>
                      <li><strong className="text-foreground">Skills and Qualifications:</strong> Technical skills, certifications, languages, accomplishments</li>
                      <li><strong className="text-foreground">Contact Information:</strong> Phone number, location, LinkedIn profile (optional)</li>
                      <li><strong className="text-foreground">Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">1.2 Information Collected Automatically</h3>
                    <p className="mb-4 text-muted-foreground">
                      When you access our Service, we automatically collect:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Usage Data:</strong> Pages visited, features used, time spent, click patterns</li>
                      <li><strong className="text-foreground">Device Information:</strong> Browser type, operating system, IP address, device identifiers</li>
                      <li><strong className="text-foreground">Cookies and Similar Technologies:</strong> Session cookies, authentication tokens, analytics cookies</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">1.3 AI Processing Data</h3>
                    <p className="mb-4 text-muted-foreground">
                      To generate your resumes and cover letters, our AI system processes:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• All professional information you provide</li>
                      <li>• Job descriptions you upload or paste</li>
                      <li>• Previous resume/cover letter content you create</li>
                      <li>• User preferences and formatting choices</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">2. How We Use Your Information</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.1 Provide Our Service</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Generate AI-powered resumes and cover letters based on your input</li>
                      <li>• Create, maintain, and secure your account</li>
                      <li>• Process your subscription payments</li>
                      <li>• Provide customer support and respond to inquiries</li>
                      <li>• Send transactional emails (account creation, password resets, receipts)</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.2 Improve Our Service</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Analyze usage patterns to improve AI quality and accuracy</li>
                      <li>• Develop new features and functionality</li>
                      <li>• Conduct research and analytics</li>
                      <li>• Fix bugs and technical issues</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.3 Communication</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Send important service updates and security alerts</li>
                      <li>• Notify you of changes to our Terms or Privacy Policy</li>
                      <li>• Send marketing emails (only if you opt in - you can unsubscribe anytime)</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.4 Legal Compliance</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Comply with legal obligations and law enforcement requests</li>
                      <li>• Protect against fraud, abuse, and security threats</li>
                      <li>• Enforce our Terms of Service</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">3. How We Share Your Information</h2>
                  <p className="mb-6 text-muted-foreground">
                    We do NOT sell your personal information to third parties. We share your information only in these limited circumstances:
                  </p>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">3.1 Service Providers</h3>
                    <p className="mb-4 text-muted-foreground">
                      We share data with trusted third-party providers who help us operate our Service:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Anthropic (Claude AI):</strong> Processes your professional information to generate resume content</li>
                      <li><strong className="text-foreground">Stripe:</strong> Processes payment information securely (they never share card details with us)</li>
                      <li><strong className="text-foreground">Hosting Providers (Vercel, AWS):</strong> Store and serve your data securely</li>
                      <li><strong className="text-foreground">Email Service (SendGrid/Postmark):</strong> Send transactional and account-related emails</li>
                      <li><strong className="text-foreground">Analytics (Vercel Analytics):</strong> Understand how users interact with our Service</li>
                    </ul>
                    <p className="mt-4 text-sm text-muted-foreground">
                      All service providers are contractually obligated to protect your data and use it only for providing services to us.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">3.2 Legal Requirements</h3>
                    <p className="mb-4 text-muted-foreground">
                      We may disclose your information if required by law, court order, or government request, or to:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Protect our legal rights</li>
                      <li>• Prevent fraud or security threats</li>
                      <li>• Comply with legal investigations</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">3.3 Business Transfers</h3>
                    <p className="text-muted-foreground">
                      If LoopBaan is acquired or merged with another company, your information may be transferred to the new owners, who must continue to honor this Privacy Policy.
                    </p>
                  </Card>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">4. How We Protect Your Information</h2>
                  <p className="mb-6 text-muted-foreground">
                    We implement industry-standard security measures:
                  </p>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">4.1 Technical Safeguards</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Encryption in Transit:</strong> All data transmitted using TLS/SSL (HTTPS)</li>
                      <li><strong className="text-foreground">Encryption at Rest:</strong> Database encryption for stored data</li>
                      <li><strong className="text-foreground">Secure Authentication:</strong> Bcrypt password hashing, secure session management</li>
                      <li><strong className="text-foreground">Access Controls:</strong> Limited employee access, role-based permissions</li>
                      <li><strong className="text-foreground">Regular Backups:</strong> Automated backups with encryption</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">4.2 Organizational Safeguards</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Employee training on data protection</li>
                      <li>• Confidentiality agreements for all staff</li>
                      <li>• Regular security audits and updates</li>
                      <li>• Incident response procedures</li>
                    </ul>
                  </Card>

                  <Card className="p-6 bg-muted/30">
                    <p className="text-sm text-muted-foreground italic">
                      <strong className="text-foreground">Important:</strong> However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
                    </p>
                  </Card>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">5. Your Privacy Rights</h2>
                  <p className="mb-6 text-muted-foreground">
                    You have the following rights regarding your personal information:
                  </p>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">5.1 Access and Portability</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Right to Access:</strong> Request a copy of all personal data we hold about you</li>
                      <li><strong className="text-foreground">Right to Portability:</strong> Download your data in a machine-readable format (JSON/PDF)</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">5.2 Correction and Deletion</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Right to Correct:</strong> Update inaccurate or incomplete information in your account settings</li>
                      <li><strong className="text-foreground">Right to Delete:</strong> Request deletion of your account and all associated data</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">5.3 Control and Opt-Out</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Right to Opt-Out:</strong> Unsubscribe from marketing emails (transactional emails still apply)</li>
                      <li><strong className="text-foreground">Right to Restrict:</strong> Limit how we process your data</li>
                      <li><strong className="text-foreground">Right to Object:</strong> Object to certain data processing activities</li>
                    </ul>
                  </Card>

                  <Card className="p-6 bg-primary/5">
                    <h3 className="mb-3 text-lg font-semibold">5.4 How to Exercise Your Rights</h3>
                    <p className="text-muted-foreground">
                      Email us at: <a href="mailto:privacy@loopbaan.ai" className="text-primary hover:underline">privacy@loopbaan.ai</a> with your request. We will respond within 30 days.
                    </p>
                  </Card>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">6. Data Retention</h2>
                  <Card className="p-6">
                    <p className="mb-4 text-muted-foreground">
                      We retain your information for as long as your account is active or as needed to provide our Service.
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Active Accounts:</strong> Data retained indefinitely while account is active</li>
                      <li><strong className="text-foreground">Deleted Accounts:</strong> Data permanently deleted within 30 days of account deletion</li>
                      <li><strong className="text-foreground">Backup Copies:</strong> May persist in backups for up to 90 days, then permanently deleted</li>
                      <li><strong className="text-foreground">Legal Requirements:</strong> May retain certain data longer if required by law</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">7. Children&apos;s Privacy</h2>
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      LoopBaan is not intended for users under 18 years of age. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, contact us immediately at <a href="mailto:privacy@loopbaan.ai" className="text-primary hover:underline">privacy@loopbaan.ai</a> and we will delete it.
                    </p>
                  </Card>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">8. International Data Transfers</h2>
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      Your information may be transferred to and processed in the United States or other countries where our service providers operate. By using our Service, you consent to such transfers. We ensure appropriate safeguards are in place to protect your data.
                    </p>
                  </Card>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">9. California Privacy Rights (CCPA)</h2>
                  <p className="mb-6 text-muted-foreground">
                    If you are a California resident, you have additional rights:
                  </p>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.1 Right to Know</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Categories of personal information collected</li>
                      <li>• Categories of sources from which information is collected</li>
                      <li>• Business or commercial purpose for collecting information</li>
                      <li>• Categories of third parties with whom we share information</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.2 Right to Delete</h3>
                    <p className="text-muted-foreground">
                      Request deletion of your personal information (subject to certain exceptions)
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.3 Right to Opt-Out of Sale</h3>
                    <p className="text-muted-foreground">
                      We do NOT sell your personal information. However, you can opt out of certain data sharing.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.4 Right to Non-Discrimination</h3>
                    <p className="text-muted-foreground">
                      We will not discriminate against you for exercising your privacy rights.
                    </p>
                  </Card>

                  <Card className="p-6 bg-primary/5">
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">To exercise these rights, email:</strong> <a href="mailto:privacy@loopbaan.ai" className="text-primary hover:underline">privacy@loopbaan.ai</a>
                    </p>
                  </Card>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">10. European Privacy Rights (GDPR)</h2>
                  <p className="mb-6 text-muted-foreground">
                    If you are in the European Economic Area (EEA), you have additional rights:
                  </p>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">10.1 Legal Basis for Processing</h3>
                    <p className="mb-4 text-muted-foreground">We process your data based on:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Consent:</strong> You have given explicit consent for specific purposes</li>
                      <li><strong className="text-foreground">Contract:</strong> Processing is necessary to fulfill our service to you</li>
                      <li><strong className="text-foreground">Legal Obligation:</strong> We must process data to comply with laws</li>
                      <li><strong className="text-foreground">Legitimate Interests:</strong> We have a legitimate business reason, balanced against your rights</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">10.2 Additional Rights</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Right to withdraw consent at any time</li>
                      <li>• Right to lodge a complaint with your supervisory authority</li>
                      <li>• Right to object to automated decision-making</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">10.3 Data Protection Officer</h3>
                    <p className="text-muted-foreground">
                      Contact our DPO at: <a href="mailto:dpo@loopbaan.ai" className="text-primary hover:underline">dpo@loopbaan.ai</a>
                    </p>
                  </Card>
                </div>

                {/* Section 11 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">11. Cookies and Tracking Technologies</h2>
                  <p className="mb-6 text-muted-foreground">
                    We use cookies and similar technologies:
                  </p>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">11.1 Essential Cookies</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Authentication:</strong> Keep you logged in</li>
                      <li><strong className="text-foreground">Security:</strong> Prevent fraud and abuse</li>
                      <li><strong className="text-foreground">Session Management:</strong> Remember your preferences</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">11.2 Analytics Cookies</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Vercel Analytics:</strong> Understand how users interact with our Service</li>
                      <li><strong className="text-foreground">Anonymous Usage Data:</strong> Improve performance and user experience</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">11.3 Your Cookie Choices</h3>
                    <p className="text-muted-foreground">
                      You can control cookies through your browser settings. However, disabling essential cookies may limit functionality.
                    </p>
                  </Card>
                </div>

                {/* Section 12 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">12. Third-Party Links</h2>
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      Our Service may contain links to external websites. We are not responsible for the privacy practices of these third-party sites. Please review their privacy policies.
                    </p>
                  </Card>
                </div>

                {/* Section 13 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">13. AI and Machine Learning</h2>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">13.1 How We Use AI</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• We use Anthropic&apos;s Claude AI to process your information and generate resume content</li>
                      <li>• Your data is sent to Anthropic&apos;s API for processing</li>
                      <li>• Anthropic does not use your data to train their AI models (per their privacy policy)</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">13.2 Data Accuracy</h3>
                    <p className="text-muted-foreground">
                      AI-generated content may contain errors. You are responsible for reviewing and verifying all content before use.
                    </p>
                  </Card>
                </div>

                {/* Section 14 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">14. Changes to This Privacy Policy</h2>
                  <Card className="p-6">
                    <p className="mb-4 text-muted-foreground">
                      We may update this Privacy Policy from time to time. Changes will be posted on this page with a new "Last Updated" date. Continued use of the Service after changes constitutes acceptance of the updated policy.
                    </p>
                    <p className="text-muted-foreground">
                      For material changes, we will notify you via email or prominent notice on our website.
                    </p>
                  </Card>
                </div>

                {/* Section 15 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">15. Contact Us</h2>
                  <Card className="bg-primary/5 p-6">
                    <p className="mb-4 text-muted-foreground">
                      If you have questions about this Privacy Policy or our privacy practices:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Email:</strong> <a href="mailto:privacy@loopbaan.ai" className="text-primary hover:underline">privacy@loopbaan.ai</a></li>
                      <li><strong className="text-foreground">Response Time:</strong> We will respond to inquiries within 30 days</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 16 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">16. Your Consent</h2>
                  <Card className="p-6 bg-muted/30">
                    <p className="text-muted-foreground">
                      By using LoopBaan, you consent to this Privacy Policy and our collection, use, and disclosure of your information as described herein.
                    </p>
                  </Card>
                </div>

                <div className="mt-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">LoopBaan.ai LLC</strong><br />
                    Last Updated: October 25, 2025
                  </p>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t bg-muted/30 py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <Link to="/" className="flex items-center gap-2">
                <Logo className="h-12 w-12" />
                <span className="font-semibold">LoopBaan.ai</span>
              </Link>
              <div className="flex gap-6 text-sm text-muted-foreground">
                <Link to="/contact" className="hover:text-foreground">Contact</Link>
                <Link to="/ats-optimization" className="hover:text-foreground">ATS Guide</Link>
                <Link to="/terms" className="hover:text-foreground">Terms</Link>
                <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
              </div>
            </div>
            <div className="mt-8 text-center text-sm text-muted-foreground">
              © 2025 LoopBaan.ai LLC. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Privacy;